import { Hero } from "@/components/site/Hero";
import { Manifesto } from "@/components/site/Manifesto";
import { Marquee } from "@/components/site/Marquee";
import { ProductShowcase } from "@/components/site/ProductShowcase";
import { ColorGrid } from "@/components/site/ColorGrid";
import { ContactCTA } from "@/components/site/ContactCTA";
import { motion } from "framer-motion";

export default function HomePage() {
    return (
        <main data-testid="page-home">
            <Hero />

            {/* Editorial marquee — slow horizontal ticker */}
            <section className="py-8 md:py-12 bg-bone border-y border-ink/10 text-ink overflow-hidden">
                <Marquee
                    items={[
                        "Made in Oman",
                        "Since Muscat",
                        "Pigment. Not filler.",
                        "Salt · Sun · Stone",
                        "Ammonia-free",
                        "Slow-tested, long-lasting",
                    ]}
                />
            </section>

            <Manifesto />

            {/* Full-bleed image / spotlight moment */}
            <section className="relative bg-ink text-bone">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32">
                    <div className="grid grid-cols-12 gap-6 items-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                            className="col-span-12 md:col-span-6"
                        >
                            <div className="relative aspect-[4/5] overflow-hidden bg-[#151517] border border-bone/10">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="absolute inset-0 bg-gradient-to-br from-brand-red/30 via-transparent to-brand-yellow/20" />
                                    <img
                                        src="/assets/logo.png"
                                        alt="Mist Paints"
                                        className="relative w-4/5 h-auto"
                                        style={{ filter: "drop-shadow(0 30px 60px rgba(237,41,56,0.35))" }}
                                    />
                                </div>
                                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-xs font-mono uppercase tracking-widest text-bone/50">
                                    <span>Signature · Tortoise Mark</span>
                                    <span>Est. Muscat</span>
                                </div>
                            </div>
                        </motion.div>

                        <div className="col-span-12 md:col-span-6 md:pl-10">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-bone/50">
                                Signature
                            </div>
                            <h3 className="mt-4 font-display text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]">
                                A tortoise, on purpose.
                            </h3>
                            <p className="mt-6 text-bone/70 text-lg max-w-md">
                                Slow, steady, and armoured with colour. Our tortoise mark is a promise:
                                every coating leaves our floor only after it has out-tested time,
                                temperature, and touch.
                            </p>

                            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
                                {[
                                    { k: "15+", v: "Product SKUs" },
                                    { k: "90+", v: "Catalogue shades" },
                                    { k: "48°C", v: "Climate-tested" },
                                ].map((s) => (
                                    <div key={s.k}>
                                        <div className="font-display text-3xl md:text-4xl text-brand-yellow">
                                            {s.k}
                                        </div>
                                        <div className="text-xs font-mono uppercase tracking-widest text-bone/50 mt-1">
                                            {s.v}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <ProductShowcase limit={6} />

            <ColorGrid compact />

            <ContactCTA />
        </main>
    );
}
